import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Drawer, Form, InputNumber, Row, Select } from "antd";
import { UNIT_CONVERTION, getItemCategoryAssignedUnitsUrl } from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import HandleNotification from "common/Notification";
import { StoreRequisitionContext } from "context/StoreRequisitionContext/StoreRequisitionContextProvider";
import debounce from "lodash/debounce";

const { Option } = Select;

const getColorFromBomItem = (bomItem) => {
  if (bomItem?.color_info?.name) {
    return {
      id: bomItem.color_info.id,
      name: bomItem.color_info.name,
    };
  }
  const colorAttr = bomItem?.item_info?.attribute_value_info?.find(
    (attr) => attr.attribute_info?.name === "Color Name or Code",
  );
  if (colorAttr?.attribute_value) {
    return { id: bomItem.color_id, name: colorAttr.attribute_value };
  }
  return null;
};

const ItemAddDrawer = ({
  open,
  onClose,
  onSave,
  styleId,
  subStyleMap = [],
  editData,
  srType,
  itemCategoryIds = [],
}) => {
  const [form] = Form.useForm();
  const { bomDetails, getBomDetailsByStyleId } = useContext(
    StoreRequisitionContext,
  );
  const [selectedBomItem, setSelectedBomItem] = useState(null);
  const [assignedUnitList, setAssignedUnitList] = useState([]);
  const lastBomFetchKeyRef = useRef("");

  const watchedCategoryId = Form.useWatch("item_category_id", form);
  const categoryIdsKey = (itemCategoryIds || []).join(",");
  const editItemKey = editData?.key ?? editData?.item_id ?? null;

  const loadAssignedUnits = useCallback(async (categoryId) => {
    if (!categoryId) {
      setAssignedUnitList([]);
      return;
    }

    const response = await getData(getItemCategoryAssignedUnitsUrl(categoryId), false, {
      update_view: 1,
    });

    if (response?.data?.code === 200) {
      const maps = response?.data?.data?.item_category_units || [];
      setAssignedUnitList(
        maps
          .map((unitMap) => ({
            id: unitMap.unit?.id ?? unitMap.unit_id,
            name: unitMap.unit?.name,
            base_unit: unitMap.unit?.base_unit,
          }))
          .filter((unit) => unit.id),
      );
    } else {
      setAssignedUnitList([]);
    }
  }, []);

  useEffect(() => {
    if (open && watchedCategoryId) {
      loadAssignedUnits(watchedCategoryId);
      return;
    }

    if (open) {
      setAssignedUnitList([]);
    }
  }, [open, watchedCategoryId, loadAssignedUnits]);

  useEffect(() => {
    if (!open || !styleId || !srType) {
      lastBomFetchKeyRef.current = "";
      return;
    }

    const fetchKey = `${styleId}-${srType}-${categoryIdsKey}`;
    if (lastBomFetchKeyRef.current === fetchKey) {
      return;
    }

    lastBomFetchKeyRef.current = fetchKey;
    getBomDetailsByStyleId(styleId, {
      sr_type: srType,
      item_category_ids: itemCategoryIds || [],
    });
  }, [open, styleId, srType, categoryIdsKey, getBomDetailsByStyleId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!editData) {
      form.resetFields();
      setSelectedBomItem(null);
      return;
    }

    form.setFieldsValue({
      item_id: editData.item_id,
      item_category_id: editData.item_category_id,
      color_id: editData.color_id,
      sub_style_id: editData.sub_style_id,
      unit_id: editData.unit_id,
      order_qty: editData.order_qty,
      convertion_rate: editData.convertion_rate,
      roll_qty: editData.roll_qty,
      convertion_unit_id: editData.convertion_unit_id,
      qty: editData.qty,
    });
  }, [open, editItemKey, form]);

  useEffect(() => {
    if (!open || !editData?.item_id || !bomDetails.length) {
      return;
    }

    const bomItem = bomDetails.find((b) => b.item_id === editData.item_id);
    if (bomItem) {
      setSelectedBomItem(bomItem);
    }
  }, [open, editData?.item_id, bomDetails]);

  const convertQtyByUnit = useCallback(
    async (convertionUnitId, orderQty, bomUnitId) => {
      if (!convertionUnitId) {
        form.setFieldsValue({ qty: null, convertion_rate: null });
        return;
      }

      if (!bomUnitId || orderQty == null || orderQty === "") {
        return;
      }

      const bodyData = {
        convert_from: Number(convertionUnitId),
        convert_to: Number(bomUnitId),
        bom_value: Number(orderQty),
      };

      const response = await postData(UNIT_CONVERTION, bodyData);

      if (response?.code === 200) {
        const convertedValue =
          response?.data?.converted_value != null
            ? Number(response.data.converted_value)
            : null;
        const conversionRate = response?.data?.conversion_rate ?? 1;

        form.setFieldsValue({
          qty:
            convertedValue != null
              ? Number(Number(convertedValue).toFixed(2))
              : null,
          convertion_rate: conversionRate,
        });
      } else {
        form.setFieldsValue({
          qty: Number(orderQty),
          convertion_rate: 1,
        });
        HandleNotification(
          "error",
          "bottomRight",
          response?.data?.message?.[0] || "Error converting unit value!",
          null,
        );
      }
    },
    [form],
  );

  const debouncedConvertQty = useMemo(
    () =>
      debounce((convertionUnitId, orderQty, bomUnitId) => {
        convertQtyByUnit(convertionUnitId, orderQty, bomUnitId);
      }, 500),
    [convertQtyByUnit],
  );

  useEffect(() => {
    return () => debouncedConvertQty.cancel();
  }, [debouncedConvertQty]);

  const handleItemSelect = async (itemId) => {
    const bomItem = bomDetails.find((b) => b.item_id === itemId);
    setSelectedBomItem(bomItem || null);
    if (bomItem) {
      const color = getColorFromBomItem(bomItem);
      const orderQty = bomItem.order_quantity;
      const convertionUnitId = bomItem.convert_unit_id;

      form.setFieldsValue({
        item_category_id: bomItem.item_category_id,
        color_id: color?.id,
        sub_style_id: bomItem.sub_style_id || undefined,
        unit_id: bomItem.unit_id,
        order_qty: orderQty,
        convertion_rate: bomItem.per_product_required_quantity,
        roll_qty: undefined,
        convertion_unit_id: convertionUnitId,
        qty: undefined,
      });

      if (convertionUnitId && bomItem.unit_id && orderQty != null) {
        await convertQtyByUnit(convertionUnitId, orderQty, bomItem.unit_id);
      } else {
        form.setFieldsValue({
          qty: bomItem.updated_required_quantity || bomItem.required_quantity,
        });
      }
    }
  };

  const handleConversionUnitChange = (convertionUnitId) => {
    const orderQty = form.getFieldValue("order_qty");
    const unitId = form.getFieldValue("unit_id");
    convertQtyByUnit(convertionUnitId, orderQty, unitId);
  };

  const handleQtyChange = (orderQty) => {
    const convertionUnitId = form.getFieldValue("convertion_unit_id");
    const unitId = form.getFieldValue("unit_id");
    debouncedConvertQty(convertionUnitId, orderQty, unitId);
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const bomItem = bomDetails.find((b) => b.item_id === values.item_id);
      const color = getColorFromBomItem(bomItem);
      const subStyle = subStyleMap.find((s) => s.id === values.sub_style_id);
      const selectedUnit = assignedUnitList.find(
        (unit) => unit.id === values.convertion_unit_id,
      );
      onSave({
        ...editData,
        item_id: values.item_id,
        item_name: bomItem?.item_info?.name || bomItem?.name,
        item_category_id: values.item_category_id,
        item_category_name:
          bomItem?.item_category_info?.name || bomItem?.name,
        color_id: values.color_id,
        color_name: color?.name,
        sub_style_id: values.sub_style_id,
        sub_style_no: subStyle?.sub_style_no,
        unit_id: values.unit_id,
        unit_name: bomItem?.unit_info?.name,
        order_qty: values.order_qty,
        convertion_rate: values.convertion_rate,
        roll_qty: values.roll_qty,
        convertion_unit_id: values.convertion_unit_id,
        convertion_unit_name:
          selectedUnit?.name || bomItem?.convert_unit_info?.name,
        qty: values.qty,
      });
      form.resetFields();
      setSelectedBomItem(null);
      onClose();
    });
  };

  const color = selectedBomItem ? getColorFromBomItem(selectedBomItem) : null;

  return (
    <Drawer
      title={editData ? "Edit Item" : "Add Item"}
      open={open}
      onClose={onClose}
      width={560}
      maskClosable={false}
      destroyOnClose
      extra={
        <Button type="primary" size="small" onClick={handleSubmit}>
          Save
        </Button>
      }
    >
      <Form form={form} layout="vertical" size="small">
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="Item"
              name="item_id"
              rules={[{ required: true, message: "Item is required" }]}
            >
              <Select
                showSearch
                placeholder="Select item"
                optionFilterProp="children"
                onChange={handleItemSelect}
              >
                {bomDetails.map((item) => (
                  <Option key={item.item_id} value={item.item_id}>
                    {item.item_info?.name || item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Item Category" name="item_category_id">
              <Select disabled placeholder="Auto populated">
                {selectedBomItem && (
                  <Option value={selectedBomItem.item_category_id}>
                    {selectedBomItem.item_category_info?.name ||
                      selectedBomItem.name}
                  </Option>
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Item Color" name="color_id">
              <Select disabled placeholder="Auto populated">
                {color && <Option value={color.id}>{color.name}</Option>}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Sub Style" name="sub_style_id">
              <Select disabled placeholder="Auto populated" allowClear>
                {subStyleMap.map((sub) => (
                  <Option key={sub.id} value={sub.id}>
                    {sub.sub_style_no}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Unit" name="unit_id">
              <Select disabled placeholder="Auto populated">
                {selectedBomItem?.unit_info && (
                  <Option value={selectedBomItem.unit_info.id}>
                    {selectedBomItem.unit_info.name}
                  </Option>
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Qty"
              name="order_qty"
              rules={[{ required: true, message: "Qty is required" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                onChange={handleQtyChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="YY" name="convertion_rate">
              <InputNumber style={{ width: "100%" }} min={0} disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Roll Qty" name="roll_qty">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Conv. Unit" name="convertion_unit_id">
              <Select
                showSearch
                placeholder="Select conversion unit"
                optionFilterProp="children"
                allowClear
                disabled={!watchedCategoryId}
                onChange={handleConversionUnitChange}
              >
                {assignedUnitList.map((unit) => (
                  <Option key={unit.id} value={unit.id}>
                    {unit.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Conversion Qty"
              name="qty"
              rules={[{ required: true, message: "Conversion qty is required" }]}
            >
              <InputNumber style={{ width: "100%" }} min={0} disabled />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
};

export default ItemAddDrawer;
